import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import useYupValidationResolver from '@/lib/hooks/yup';
import { useContentTracking } from '@/lib/hooks/tracking';
import { ModalComponentBaseProps } from '@pcmp/react-providers';
import { Sheets, ModalHeader, ModalFooter, ModalBody } from '@ingka/modal';
import Button from '@ingka/button';
import ControlledInput from '@/components/forms/controlled-input';
import {
  createInvitationSchema,
  CreateInvitationFormData,
} from '@/lib/schemas/create-invitation';

type CreateInvitationSiderProps = ModalComponentBaseProps & {
  onCreate: (data: CreateInvitationFormData) => Promise<boolean>;
};

const CreateInvitationSider: FC<CreateInvitationSiderProps> = ({
  handleDismiss,
  onCreate,
  ...rest
}) => {
  // Hooks
  const { t } = useTranslation();
  const trackContent = useContentTracking();

  const resolver = useYupValidationResolver(createInvitationSchema);
  const form = useForm<CreateInvitationFormData>({
    mode: 'all',
    resolver,
    defaultValues: {
      email: '',
      name: '',
    },
    criteriaMode: 'all',
  });

  const isSubmitting = useRef(false);
  const isDisabled = !form.formState.isValid || isSubmitting.current;

  const submitForm = async () => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    try {
      const formData = form.getValues();
      const success = await onCreate(formData);

      if (success) handleDismiss();
    } finally {
      isSubmitting.current = false;
    }
  };

  useEffect(() => {
    trackContent('create-invitation', {
      type: 'sider',
    });
  }, [trackContent]);

  return (
    <Sheets
      {...rest}
      aria-label={t('page.admin.invitations.title')}
      header={
        <ModalHeader
          ariaCloseTxt={t('aria.close')}
          title={t('page.admin.invitations.title')}
        />
      }
      footer={
        <ModalFooter>
          <Button
            text={t('label.cancel')}
            type="secondary"
            onClick={handleDismiss}
          />
          <Button
            disabled={isDisabled}
            aria-label={t('label.confirm')}
            text={t('label.confirm')}
            type="primary"
            onClick={submitForm}
          />
        </ModalFooter>
      }
    >
      <ModalBody>
        <p className="mb-4">{t('page.admin.invitations.description')}</p>

        <FormProvider {...form}>
          <ControlledInput<CreateInvitationFormData>
            name="name"
            label={t('label.name')}
            type="text"
          />

          <ControlledInput<CreateInvitationFormData>
            name="email"
            label={t('label.email')}
            type="email"
          />
        </FormProvider>
      </ModalBody>
    </Sheets>
  );
};

export default CreateInvitationSider;
